import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Award, Plus, RotateCcw, X } from 'lucide-react';
import { problemDetail } from '@/shared/api/problem';
import {
  getGetApiV1PointRulesQueryKey,
  usePostApiV1PointRulesCodeActive,
  usePutApiV1PointRules,
  useGetApiV1PointRules,
} from '@/shared/api/generated/scoring/scoring';
import type { PointRuleDto } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { QueryError } from '@/shared/ui/QueryError';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Panel } from '@/shared/ui/Panel';
import { Chip } from '@/shared/ui/Chip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { TableSkeleton } from '@/shared/ui/TableSkeleton';
import { EmptyState } from '@/shared/ui/EmptyState';

/**
 * The scoring table (§23.6).
 *
 * ⚠️ **The client's answer is that new items and their values are added from the dashboard** — which
 * is why this is a table rather than an enum, and why it needed a screen. It was seeded on the first
 * migration and had none until 25 August 2026: the three values the association gave could only be
 * changed with a database session.
 */
export function PointsTable() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();

  const { data, isPending, isError, error, refetch } = useGetApiV1PointRules();

  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getGetApiV1PointRulesQueryKey() });

  const save = usePutApiV1PointRules({
    mutation: {
      onSuccess: () => {
        setFailure(null);
        setAdding(false);
        void invalidate();
      },
      onError: (problem) => setFailure(problemDetail(problem) ?? t.points.saveFailed),
    },
  });

  const setActive = usePostApiV1PointRulesCodeActive({
    mutation: {
      onSuccess: () => void invalidate(),
      onError: (problem) => setFailure(problemDetail(problem) ?? t.points.saveFailed),
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader title={t.points.title} lede={t.points.subtitle} />

      {isError && <QueryError error={error} onRetry={() => void refetch()} />}

      {failure && (
        <p role="alert" className="rounded-lg bg-error-50 p-3 text-sm text-error-600">
          {failure}
        </p>
      )}

      <Panel
        title={t.points.heading}
        actions={
          <Button onClick={() => setAdding((value) => !value)}>
            {adding ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {adding ? t.points.cancel : t.points.add}
          </Button>
        }
        bodyClassName="flex flex-col"
      >
        {adding && (
          <NewItem
            onSave={(item) => save.mutate({ data: item })}
            pending={save.isPending}
          />
        )}

        {/*
          ⚠️ A refused query must not answer «لم يُضَف أيّ بند بعد». §23.6 makes this table the
          association's own decision about what an activity is worth, so an empty state here says
          they decided nothing — and on 2 September 2026 it said exactly that to a head of control
          whose 403 was showing two lines above, over a table holding three live rules.
        */}
        {isError ? null : isPending ? (
          <TableSkeleton rows={4} columns={4} />
        ) : data && data.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.points.item}</TableHead>
                <TableHead>{t.points.code}</TableHead>
                <TableHead>{t.points.value}</TableHead>
                <TableHead>{t.points.state}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((rule) => (
                <Rule
                  key={rule.id}
                  rule={rule}
                  locale={locale}
                  pending={save.isPending || setActive.isPending}
                  onRetarget={(points) =>
                    save.mutate({
                      data: {
                        code: rule.code!,
                        nameAr: rule.nameAr!,
                        nameEn: rule.nameEn!,
                        points,
                      },
                    })
                  }
                  onSetActive={(isActive) =>
                    setActive.mutate({ code: rule.code!, data: { isActive } })
                  }
                />
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState icon={Award} title={t.points.emptyTitle} hint={t.points.empty} />
        )}
      </Panel>
    </div>
  );
}

/**
 * One row, with its value editable in place.
 *
 * ⚠️ **In place rather than behind a dialog.** Changing what a mission is worth is one number, and
 * §23.6 expects the association to tune it — a modal per row makes comparing three values against
 * each other, which is the actual task, into three round trips through a form.
 */
function Rule({
  rule,
  locale,
  pending,
  onRetarget,
  onSetActive,
}: {
  rule: PointRuleDto;
  locale: string;
  pending: boolean;
  onRetarget: (points: number) => void;
  onSetActive: (isActive: boolean) => void;
}) {
  const { t } = useLocale();
  const [points, setPoints] = useState(String(rule.points ?? 0));

  const changed = points.trim() !== String(rule.points ?? 0);
  const valid = /^\d+$/.test(points.trim());

  return (
    <TableRow className={rule.isActive ? undefined : 'opacity-60'}>
      <TableCell className="text-sm">
        {locale === 'ar' ? rule.nameAr : rule.nameEn}
      </TableCell>

      {/* ⚠️ Left to right: the code is a machine key somebody types into a query, and an Arabic
          line mirrors the hyphens in it. */}
      <TableCell dir="ltr" className="text-start font-mono text-xs text-gray-500">
        {rule.code}
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2">
          <input
            aria-label={`${t.points.value} — ${rule.code}`}
            inputMode="numeric"
            dir="ltr"
            className="h-10 w-24 rounded-lg border border-gray-200 bg-white px-3 text-start text-sm outline-none focus:border-brand-300"
            value={points}
            onChange={(event) => setPoints(event.target.value)}
          />

          {changed && (
            <Button
              disabled={!valid || pending}
              onClick={() => onRetarget(Number(points.trim()))}
            >
              {t.points.save}
            </Button>
          )}
        </div>
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2">
          <Chip tone={rule.isActive ? 'approved' : 'neutral'}>
            {rule.isActive ? t.points.active : t.points.retired}
          </Chip>

          {/* ⚠️ Retire, never delete. Points already awarded under an item must stay traceable to
              it — a volunteer's total has to be explicable a year later. */}
          <Button
            variant="ghost"
            disabled={pending}
            onClick={() => onSetActive(!rule.isActive)}
          >
            {rule.isActive ? (
              t.points.retire
            ) : (
              <>
                <RotateCcw className="h-4 w-4" />
                {t.points.restore}
              </>
            )}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function NewItem({
  onSave,
  pending,
}: {
  onSave: (item: { code: string; nameAr: string; nameEn: string; points: number }) => void;
  pending: boolean;
}) {
  const { t } = useLocale();
  const [code, setCode] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [points, setPoints] = useState('');

  const valid =
    /^[a-z0-9-]+$/.test(code.trim()) && nameAr.trim() && nameEn.trim() && /^\d+$/.test(points.trim());

  return (
    <form
      className="mb-4 grid gap-3 rounded-lg bg-gray-50 p-4 sm:grid-cols-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          code: code.trim(),
          nameAr: nameAr.trim(),
          nameEn: nameEn.trim(),
          points: Number(points.trim()),
        });
      }}
    >
      <Field label={t.points.code} htmlFor="new-code" hint={t.points.codeHint}>
        {/* ⚠️ The code is the key an award references forever, so it is typed left to right and
            constrained here as well as on the server: a code with a space in it works until
            somebody has to type it into a query a year later. */}
        <input
          id="new-code"
          dir="ltr"
          className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-start font-mono text-sm outline-none focus:border-brand-300"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </Field>

      <Field label={t.points.nameAr} htmlFor="new-name-ar">
        <input
          id="new-name-ar"
          className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-brand-300"
          value={nameAr}
          onChange={(event) => setNameAr(event.target.value)}
        />
      </Field>

      <Field label={t.points.nameEn} htmlFor="new-name-en">
        <input
          id="new-name-en"
          dir="ltr"
          className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-start text-sm outline-none focus:border-brand-300"
          value={nameEn}
          onChange={(event) => setNameEn(event.target.value)}
        />
      </Field>

      <Field label={t.points.value} htmlFor="new-points">
        <div className="flex gap-2">
          <input
            id="new-points"
            inputMode="numeric"
            dir="ltr"
            className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-start text-sm outline-none focus:border-brand-300"
            value={points}
            onChange={(event) => setPoints(event.target.value)}
          />
          <Button type="submit" disabled={!valid || pending}>
            {t.points.save}
          </Button>
        </div>
      </Field>
    </form>
  );
}
